import React, { useState, useRef, useEffect, forwardRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faDownload } from '@fortawesome/free-solid-svg-icons';
import Toast from '../../Toast/Toast';
import AWS from 'aws-sdk';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import config from '../../../../../../application/config/config';
import Resizer from 'react-image-file-resizer';
import './MultiFileUploadS3.css';
import imageCompression from 'browser-image-compression';
import { faPaperclip } from '@fortawesome/free-solid-svg-icons';

AWS.config.update({
  accessKeyId: config.bucketS3.accessKeyId,
  secretAccessKey: config.bucketS3.secretAccessKey,
  region: config.bucketS3.region,
});

const MultiFileUploadS3 = forwardRef((props, ref) => {
  const [files, setFiles] = useState(props.file || []);
  const [uploadProgress, setUploadProgress] = useState(null);
  const uploadRef = useRef(null);

  const limitSize = props.limitFileSize ? props.limitFileSize : 10;

  const resizeFile = (file) => new Promise((resolve, reject) => {
    const fileExtension = file.name.split('.').pop().toLowerCase();

    let format;
    switch (fileExtension) {
      case 'png':
        format = 'PNG';
        break;
      case 'jpeg':
      case 'jpg':
        format = 'JPEG';
        break;
      default:
        format = 'JPEG';
    }

    let quality = 90;
    if (file.size > 1024 * 1024) {
      quality = 70;
    } else if (file.size > 2 * 1024 * 1024) {
      quality = 50;
    }

    const url = URL.createObjectURL(file);
    let img = new Image();
    img.onload = () => {
      if (format === 'JPEG') {
        Resizer.imageFileResizer(
          file,
          img.width,
          img.height,
          format,
          quality,
          0,
          (uri) => {
            resolve(uri);
          },
          'blob'
        );
      } else {
        compressPNG(file).then(compressedFile => {
          resolve(compressedFile);
        }).catch(error => {
          reject(error);
        });
      }

      URL.revokeObjectURL(url);
    };

    img.onerror = (error) => {
      reject(error);
    };
    img.src = url;
  });

  const compressPNG = async (file) => {
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      fileType: 'image/png'
    };

    try {
      const compressedFile = await imageCompression(file, options);
      return compressedFile;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const checkVideoDuration = (file) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(video.duration);
    };

    video.onerror = (error) => {
      URL.revokeObjectURL(url);
      reject(error);
    };

    video.src = url;
  });

  const handleChange = async (event) => {
    if (props.disabled) {
      Toast.error(`Upload de arquivo desabilitado.`);
      return;
    }

    const uploadedFiles = Array.from(event.target.files);

    uploadedFiles.forEach(file => {
      console.log(file.name + " - Tamanho original (KB):", file.size / 1024);
    });

    if (uploadedFiles.length + files.length > props.uploadFileLimit) {
      Toast.error(`Você não pode inserir mais do que ${props.uploadFileLimit} arquivos.`);
      return;
    }

    const tooLargeFiles = uploadedFiles.filter((file) => file.size / (1024 * 1024) > limitSize);

    if (tooLargeFiles.length > 0) {
      Toast.error(
        `Os seguintes arquivos não podem ser maiores que ${limitSize} MB(s): ${tooLargeFiles
          .map((file) => file.name)
          .join(', ')}`
      );
      return;
    }

    const currentDate = moment().format('YYYY/MM/DD');
    const s3 = new AWS.S3();

    const checkIsVideo = (file) => {
      const fileName = file.name;
      const fileExtension = fileName.split('.').pop();
      const isVideo = ['mp4', 'mkv', 'avi', 'mov'].includes(fileExtension.toLowerCase());
      return isVideo;
    }

    const filesData = await Promise.all(
      uploadedFiles.map(async (file) => {
        const fileName = file.name;
        const fileExtension = fileName.split('.').pop();
        const isImage = ['jpeg', 'jpg', 'png'].includes(fileExtension.toLowerCase());
        const isVideo = checkIsVideo(file);

        if (isImage && props.imageCompress !== false) {
          try {
            const originalFile = file;
            file = await resizeFile(file);
            console.log(originalFile.name + " - Tamanho após compressão (KB):", file.size / 1024);
          } catch (err) {
            Toast.error('Erro ao redimensionar a imagem.');
            return;
          }
        }

        if (isVideo) {
          try {
            const duration = await checkVideoDuration(file);
            if (duration > props.maxDuration) {
              Toast.error(`O vídeo excede a duração máxima permitida de ${props.maxDuration} segundos.`);
              return null;
            }
          } catch (err) {
            console.log("🚀 ~ uploadedFiles.map ~ err:", err)
            Toast.error('Erro ao verificar a duração do vídeo.');
            return null;
          }
        }

        const uniqueId = uuidv4();
        let fileUrl = `${props.path}/uploads/${currentDate}/${uniqueId}.${fileExtension}`;

        if (config.bucketS3 && config.bucketS3.path_default && config.bucketS3.path_default != '') {
          fileUrl = `${config.bucketS3.path_default}/${props.path}/uploads/${currentDate}/${uniqueId}.${fileExtension}`;
        } else {
          Toast.error('Erro ao fazer o upload do arquivo. É necessário informar o path_default nas configurações.');
          return;
        }

        const params = {
          Bucket: config.bucketS3.bucket,
          Key: fileUrl,
          Body: file,
        };

        try {
          const s3Upload = s3.upload(params);

          s3Upload.on('httpUploadProgress', (progress) => {
            const uploadProgress = Math.round((progress.loaded / progress.total) * 100);
            setUploadProgress(uploadProgress);
          });

          const data = await s3Upload.promise();
          setUploadProgress(null);
          return {
            filename: fileName,
            files3: data.Location,
          };
        } catch (error) {
          Toast.error('Erro ao fazer o upload do arquivo.');
          return null;
        }
      })
    );

    const uploadedFilesData = filesData.filter(Boolean);
    setFiles((prevFiles) => [...prevFiles, ...uploadedFilesData]);
    props.onUpload(props.name, [...files, ...uploadedFilesData]);
  };

  const deleteFile = async (fileUrl) => {
    const fileToDelete = files.find((file) => file.files3 === fileUrl);
    try {
      const s3 = new AWS.S3();
      const bucketName = config.bucketS3.bucket;
      const key = fileUrl.substring(fileUrl.indexOf(bucketName) + bucketName.length + 1);

      const params = {
        Bucket: bucketName,
        Key: key,
      };

      await s3.deleteObject(params).promise();
      setFiles((prevFiles) => prevFiles.filter((file) => file.files3 !== fileUrl));
      if (typeof props.onRemove === 'function') {
        props.onRemove(props.name, fileToDelete);
      }
    } catch (error) {
      Toast.error('Erro ao excluir o arquivo.');
    }
  };

  const clickRemove = (fileUrl) => {
    deleteFile(fileUrl);
  };

  const extensionVideos = ['mp4', 'mov', 'avi'];

  return (
    <div style={{ marginTop: 10 }} ref={ref}>
      {!props.disabled && (
        <div>
          <label className={props.useIcon ? "m-0" : "btn btn-primary"}>
            <input
              id="my-file-selector"
              className="d-none"
              type="file"
              name={props.name}
              onChange={handleChange}
              accept={props.accept}
              multiple
              autoFocus={props.autoFocus ? true : false}
            />
            {props.useIcon ? (
              <FontAwesomeIcon icon={faPaperclip} style={{
                color: "#007BFF",
                width: "1.5rem",
                height: "1.5rem",
              }} />
            ) : props.titleButton}
          </label>
          {props.error && (
            <div
              className="error invalid-feedback"
              style={{ display: 'inline', width: '100%', marginLeft: '10px' }}
            >
              {props.error}
            </div>
          )}
        </div>
      )}
      {uploadProgress !== null && (
        <div style={{ width: '100%', marginBottom: 10 }}>
          <div className="progress">
            <div
              className="progress-bar"
              role="progressbar"
              style={{ width: `${uploadProgress}%` }}
              aria-valuenow={uploadProgress}
              aria-valuemin="0"
              aria-valuemax="100"
            >
              {`${uploadProgress}%`}
            </div>
          </div>
        </div>
      )}
      {(props.showUploads !== false) && files.length > 0 && (
        <table className="file-table">
          <thead>
            <tr>
              <th style={{ textAlign: 'center' }}>Prévia</th>
              <th>Arquivo</th>
              <th style={{ textAlign: 'center' }}>Download</th>
              {!props.disabled && <th style={{ textAlign: 'center' }}>Excluir</th>}
            </tr>
          </thead>
          <tbody>
            {files.map((fileData) => {
              const fileExtension = fileData.files3?.split('.').pop().toLowerCase();
              const isVideo = extensionVideos.includes(fileExtension);
              return (
              <tr key={fileData.files3}>
                <td>
                  {isVideo ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <video width="220" height="200" controls>
                        <source src={fileData.files3} type={`video/${fileExtension}`} />
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img src={fileData.files3} style={{ maxWidth: '220px', maxHeight: '100px' }}/>
                    </div>
                  )}
                </td>
                <td>{fileData.filename}</td>
                <td style={{ textAlign: 'center' }}>
                {isVideo ? (
                  <td style={{ textAlign: 'center' }}>
                    <a style={{
                      backgroundColor: "#282c34",
                      padding: "0.4rem",
                      borderRadius: "0.3rem",
                      color: "white",
                    }} href={fileData.files3} download>
                      <FontAwesomeIcon icon={faDownload} />
                    </a>
                  </td>
                ) : (
                  <td style={{ textAlign: 'center' }}>
                    <a style={{
                      backgroundColor: "#282c34",
                      padding: "0.4rem",
                      borderRadius: "0.3rem",
                      color: "white",
                    }} href={fileData.files3} target="_blank" rel="noopener noreferrer">
                      <FontAwesomeIcon icon={faDownload} />
                    </a>
                  </td>
                )}
                </td>
                {!props.disabled && (
                  <td style={{ textAlign: 'center' }}>
                    <button style={{
                      backgroundColor: "#dd4d45",
                      padding: "0.3rem 0.5rem",
                      borderRadius: "0.3rem",
                      color: "white",
                      border: "none"
                    }} type="button" onClick={() => clickRemove(fileData.files3)}>
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </td>
                )}
              </tr>
            )})}
          </tbody>
        </table>
      )}
    </div>
  );
});

MultiFileUploadS3.defaultProps = {
  onChange: () => { },
  onUpload: () => { },
  beforeUpload: () => { },
  path: '',
  accept: 'media_type',
  titleButton: 'Anexar',
  uploadFileLimit: 3,
  file: [],
  limitFileSize: 300,
  disabled: false,
  imageCompress: true,
  maxDuration: 60
};

export default MultiFileUploadS3;